import React, {useEffect, useMemo, useRef, useState} from 'react';
import {flushSync} from 'react-dom';
import {Box} from '@mui/material';
import {keyframes} from '@mui/material/styles';
import {Scene, SceneTransforms} from '../../../scene/types';
import {ImageValue} from '../../../models/Template';
import {Box2D, clampRectToCanvas, collectInputRects, FillField, isFieldFilled, subtractRect} from './fieldList';
import NumberChip from './NumberChip';

/** Pulsing dashed border for to-do markers (prototype @keyframes glowPulse). */
const glowPulse = keyframes`
  0%, 100% { opacity: .45; }
  50% { opacity: 1; }
`;

/** How far the marker box sits outside the element bounds, in rendered px, so it
 *  reads as a target around the content rather than covering it. */
const INSET = 2;

/** Clearance the selection-mode overlay leaves around the repositioned element,
 *  in rendered px. The Konva Transformer draws its resize anchors centered ON
 *  the element's bounding box — half of every white handle (plus its enlarged
 *  touch hit area) lies OUTSIDE the box. Any overlay div covering that band
 *  swallows the touch before the canvas sees it: the handle-drag never arms and
 *  the lift's click cancels the selection instead. */
const HANDLE_CLEARANCE = 28;
/** Extra clearance above the box: the rotate handle hangs ~50px over the top
 *  edge (Konva's default rotateAnchorOffset) plus its own touch hit area. */
const ROTATE_CLEARANCE = 76;

/** Hold this long on an image marker to jump straight into reposition. */
const LONG_PRESS_MS = 500;
/** Cancel the long-press if the finger travels more than this (a scroll, not a hold). */
const MOVE_CANCEL_PX = 10;

interface CanvasMarkersProps {
    scene: Scene;
    /** User drag/pinch geometry overrides (keyed by element id) — marker boxes
     *  must wrap the element where the user MOVED it, not its original spot. */
    transforms?: SceneTransforms;
    /** Ordered checklist fields — the marker number is the 1-based index here,
     *  identical to the card numbering. */
    fields: FillField[];
    /** Card numbers by field key, when they are not the index (a row's fields
     *  are numbered within the row). */
    numbers?: ReadonlyMap<string, number>;
    selections: Record<string, string | ImageValue>;
    /** The rendered scene element (#template-scene) — measured for scale AND used
     *  as the synthetic-event sink for the long-press → drag relay. */
    canvasRef: React.RefObject<HTMLDivElement>;
    /** The position:relative wrapper the overlay is absolutely placed within. */
    wrapperRef: React.RefObject<HTMLDivElement>;
    /** The input key currently in reposition (selectedImageData.elementKey), or
     *  null. While set, the overlay switches to "selection mode": transparent
     *  cancel strips cover everything around the selected element (tap = cancel
     *  selection), and the OTHER image fields keep invisible hit areas so a
     *  long-press on them switches the selection. The selected element's own
     *  area stays uncovered — Konva owns its drag. */
    repositionKey?: string | null;
    /** The input key whose TEXT sheet is open. Same selection semantics as
     *  reposition: the edited element gets an active border, cancel strips
     *  around it close the sheet, and tapping another marker switches to it. */
    editingKey?: string | null;
    /** Tap anywhere on the canvas outside the selected element → cancel. */
    onCancelSelection?: () => void;
    /** Tap a marker → open the same flow the matching card uses. */
    onMarkerTap: (field: FillField) => void;
    /** Long-press an IMAGE marker → jump straight to reposition (skips the sheet). */
    onMarkerLongPress?: (field: FillField) => void;
}

interface Geom {
    left: number;
    top: number;
    scale: number;
}

interface MarkerProps {
    field: FillField;
    number: number;
    done: boolean;
    chipSize: number;
    chipLeft: number;
    chipTop: number;
    /** Physical geometry (left/top/width/height) — see the inline-style note below. */
    boxStyle: React.CSSProperties;
    /** Relay bridge is running on this marker → render it invisible but keep it
     *  mounted + hit-testable (it holds pointer capture and relays the drag). */
    relaying: boolean;
    /** Selection mode hit-area: no border/chip, but still tappable/holdable. */
    invisible?: boolean;
    /** This element is the one being edited right now — solid bright border so
     *  it's obvious which element the open sheet belongs to. */
    active?: boolean;
    canvasRef: React.RefObject<HTMLDivElement>;
    onTap: (field: FillField) => void;
    onLongPress?: (field: FillField) => void;
    /** Tell the parent to keep ONLY this marker while the relay runs. */
    onRelayStart?: (field: FillField) => void;
    onRelayEnd?: () => void;
}

/** Konva 9's drag-and-drop listens on `window` for `mousemove`/`mouseup` (not
 *  pointer events) and reads clientX/clientY, so the relay speaks MouseEvents.
 *  A MouseEvent has no pointerId → Konva resolves it to a stable id (999) for
 *  arm + move + end, keeping the drag element's pointer consistent. */
const fireMouse = (
    type: 'mousedown' | 'mousemove' | 'mouseup',
    target: EventTarget | null,
    x: number,
    y: number,
) => {
    if (!target) return;
    target.dispatchEvent(new MouseEvent(type, {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX: x,
        clientY: y,
        button: 0,
        buttons: type === 'mouseup' ? 0 : 1,
    }));
};

/** One marker box. Image markers add a long-press → reposition gesture that flows
 *  straight into dragging (no lift-and-retouch): on fire we capture the pointer,
 *  ask the parent to enter reposition, then relay the SAME pointer stream to the
 *  Konva canvas as synthetic mouse events. Text markers stay click-only. */
const Marker: React.FC<MarkerProps> = ({
                                           field,
                                           number,
                                           done,
                                           chipSize,
                                           chipLeft,
                                           chipTop,
                                           boxStyle,
                                           relaying,
                                           invisible,
                                           active,
                                           canvasRef,
                                           onTap,
                                           onLongPress,
                                           onRelayStart,
                                           onRelayEnd,
                                       }) => {
    const boxRef = useRef<HTMLDivElement>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();
    const startRef = useRef<{ x: number; y: number } | null>(null);
    const lastPointRef = useRef<{ x: number; y: number }>({x: 0, y: 0});
    const pointerIdRef = useRef<number>(-1);
    const downAtRef = useRef(0);
    const movedRef = useRef(false);
    // Set when a long-press fires so the trailing click doesn't ALSO open the sheet.
    const longFiredRef = useRef(false);
    // True from long-press fire until the real pointer lifts — the relay window.
    const relayActiveRef = useRef(false);
    // Tears down the active relay's window listeners; set while a relay is live so
    // unmount can call it (a mid-drag design switch / markers-toggle removes this
    // node before pointerup fires, which would otherwise leak the global listeners).
    const relayCleanupRef = useRef<(() => void) | null>(null);
    // Both text and image markers long-press → reposition: hold arms the
    // timer + pointer-relay that drags the bound Konva node (text nodes are
    // draggable when selected, same as image pan).
    const canLongPress = !!onLongPress;

    const clearTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
        }
    };
    useEffect(() => () => {
        clearTimer();
        relayCleanupRef.current?.();
    }, []);

    // Konva's DOM event sink: the .konvajs-content div (where the stage binds its
    // listeners), falling back to the canvas / container.
    const contentEl = (): HTMLElement | null => {
        const root = canvasRef.current;
        if (!root) return null;
        return (root.querySelector('.konvajs-content') as HTMLElement | null)
            || (root.querySelector('canvas') as HTMLElement | null)
            || root;
    };

    const handlePointerDown = (e: React.PointerEvent) => {
        // Second finger of a pinch — the renderer's window-level pinch handler
        // owns it. Arming a long-press timer here would switch the selection to
        // this marker's field mid-zoom.
        if (!e.isPrimary) return;
        pointerIdRef.current = e.pointerId;
        lastPointRef.current = {x: e.clientX, y: e.clientY};
        downAtRef.current = Date.now();
        movedRef.current = false;
        longFiredRef.current = false;
        startRef.current = {x: e.clientX, y: e.clientY};
        if (!canLongPress) return;
        clearTimer();
        const pointerId = e.pointerId;
        timerRef.current = setTimeout(() => {
            longFiredRef.current = true;
            relayActiveRef.current = true;
            // Relay from WINDOW-level capture listeners, NOT from this element:
            // the reposition re-render replaces this marker's DOM node, and
            // WebKit/iOS kills a captured pointer stream with its node (Chromium
            // forgives; Safari doesn't — reproduced in the WebKit engine). Once
            // the node dies the live pointer retargets by hit-test and its events
            // still bubble to window, so these listeners survive the gesture.
            const relayMove = (ev: PointerEvent) => {
                if (ev.pointerId !== pointerId) return;
                fireMouse('mousemove', window, ev.clientX, ev.clientY);
            };
            const relayEnd = (ev: PointerEvent) => {
                if (ev.pointerId !== pointerId) return;
                cleanupRelay();
                fireMouse('mouseup', window, ev.clientX, ev.clientY);
                onRelayEnd?.();
            };
            // Markers keep touch-action: pan-y so normal swipes scroll the page.
            // touch-action can't change mid-gesture, but CANCELING touchmove (a
            // non-passive listener) does stop the browser from scrolling — so we
            // block scrolling only from the moment the hold turns into a drag.
            const preventScroll = (ev: TouchEvent) => {
                if (ev.cancelable) ev.preventDefault();
            };
            const cleanupRelay = () => {
                window.removeEventListener('pointermove', relayMove, true);
                window.removeEventListener('pointerup', relayEnd, true);
                window.removeEventListener('pointercancel', relayEnd, true);
                window.removeEventListener('touchmove', preventScroll, {capture: true} as EventListenerOptions);
                relayActiveRef.current = false;
                relayCleanupRef.current = null;
            };
            relayCleanupRef.current = cleanupRelay;
            window.addEventListener('pointermove', relayMove, true);
            window.addEventListener('pointerup', relayEnd, true);
            window.addEventListener('pointercancel', relayEnd, true);
            window.addEventListener('touchmove', preventScroll, {passive: false, capture: true});

            // Force the reposition state to COMMIT before arming: with async
            // batching, a rAF can fire before React commits the draggable flag
            // to the Konva node (reproduced on WebKit — the synthetic mousedown
            // landed on a not-yet-draggable node and the drag never armed).
            flushSync(() => {
                onRelayStart?.(field);   // parent bookkeeping (marker visuals)
                onLongPress?.(field);    // upstream: enter reposition (node becomes draggable)
            });
            const {x, y} = lastPointRef.current;
            // Arm the Konva drag; verify it took (via the Konva global's drag
            // registry) and retry a couple of times — belt-and-braces against
            // engines where the commit→Konva-prop application still lags.
            const armed = () => {
                const K = (window as any).Konva;
                return !!K?.DD?._dragElements?.size;
            };
            const arm = (attempt: number) => {
                if (!relayActiveRef.current) return;
                fireMouse('mousedown', contentEl(), x, y);
                if (!armed() && attempt < 3) {
                    setTimeout(() => arm(attempt + 1), 50);
                }
            };
            arm(0);
        }, LONG_PRESS_MS);
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        lastPointRef.current = {x: e.clientX, y: e.clientY};
        if (relayActiveRef.current) return; // the window listeners own the stream
        if (!startRef.current) return;
        const dx = e.clientX - startRef.current.x;
        const dy = e.clientY - startRef.current.y;
        if (Math.hypot(dx, dy) > MOVE_CANCEL_PX) {
            movedRef.current = true; // a scroll/drag, not a hold
            clearTimer();
        }
    };

    const handlePointerEnd = () => {
        // During a relay the window-level pointerup handler owns the ending.
        clearTimer();
    };

    const handleClick = () => {
        if (longFiredRef.current) {
            longFiredRef.current = false;
            return;
        }
        onTap(field);
    };

    return (
        <Box
            ref={boxRef}
            data-testid={`fill-marker-${field.key}`}
            onClick={handleClick}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
            // Geometry goes through the plain style prop, NOT sx: the app's RTL
            // emotion cache (stylis-plugin-rtl) rewrites `left`→`right` in generated
            // CSS, which mirrored every marker. Inline styles bypass emotion.
            style={boxStyle}
            sx={{
                position: 'absolute',
                pointerEvents: 'auto',
                // pan-y normally: swipes starting on ANY marker scroll the page.
                // A hold that becomes a drag blocks scrolling via the relay's
                // non-passive touchmove preventDefault (see handlePointerDown) —
                // touch-action alone can't do both. In selection mode (invisible
                // hit-areas over the board while repositioning) the board owns
                // ALL gestures: pan-y would let Safari claim a pinch finger for
                // scrolling before the renderer's pinch handler sees it.
                touchAction: invisible ? 'none' : 'pan-y',
                cursor: 'pointer',
                userSelect: 'none',
                WebkitTouchCallout: 'none',
            }}
        >
            {!relaying && !invisible && (
                <>
                    {/* Border is its own layer so the pulse doesn't fade the chip. */}
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '6px',
                            // Active = the element whose sheet is open right now:
                            // solid bright border + glow, unmistakably "this one".
                            border: active
                                ? '2px solid #2ea6e6'
                                : done ? '1.5px solid rgba(23,196,127,.45)' : '1.5px dashed #2ea6e6',
                            boxShadow: active ? '0 0 12px rgba(46,166,230,.65)' : 'none',
                            animation: active || done ? 'none' : `${glowPulse} 1.8s ease-in-out infinite`,
                            pointerEvents: 'none',
                        }}
                    />
                    <NumberChip
                        key={done ? 'done' : 'todo'}
                        number={number}
                        done={done}
                        layer={field.source === 'layer'}
                        chipSize={chipSize}
                        borderColor="#071726"
                        style={{position: 'absolute', top: chipTop, left: chipLeft}}
                    />
                </>
            )}
        </Box>
    );
};

/** HTML overlay over the canvas preview: one numbered marker per bound fill
 *  field. Scene coordinates are scaled to rendered pixels by measuring the
 *  rendered canvas (scale = renderedWidth / scene.width). Markers are DOM divs;
 *  the parent passes `hidden` (not an unmount) during reposition so a long-press
 *  drag relay survives. */
const CanvasMarkers: React.FC<CanvasMarkersProps> = ({
                                                         scene,
                                                         transforms,
                                                         fields,
                                                         numbers,
                                                         selections,
                                                         canvasRef,
                                                         wrapperRef,
                                                         repositionKey,
                                                         editingKey,
                                                         onCancelSelection,
                                                         onMarkerTap,
                                                         onMarkerLongPress,
                                                     }) => {
    const [geom, setGeom] = useState<Geom | null>(null);
    // The field.key whose marker is currently relaying its pointer into a drag.
    const [relayKey, setRelayKey] = useState<string | null>(null);
    // When a relay drag ends, the browser fires a trailing click wherever the
    // finger lifted — over a cancel strip or another marker, that click would
    // instantly cancel the selection the drag just made. Swallow clicks briefly.
    const relayEndAtRef = useRef(0);
    const clickSuppressed = () => Date.now() - relayEndAtRef.current < 500;
    const rects = useMemo(() => collectInputRects(scene, transforms), [scene, transforms]);

    // Track the rendered canvas box relative to the overlay wrapper. Recompute
    // on any resize (design switch, mobile fit, window resize) so markers stay
    // pinned to the scaled content. Deliberately a PASSIVE effect: React attaches
    // refs bottom-up, so during this component's layout phase the parent
    // wrapper's ref is still null — a layout effect would bail before wiring the
    // ResizeObserver and never recover.
    useEffect(() => {
        const canvas = canvasRef.current;
        const wrapper = wrapperRef.current;
        if (!canvas || !wrapper) return;

        const measure = () => {
            const cRect = canvas.getBoundingClientRect();
            const wRect = wrapper.getBoundingClientRect();
            if (!cRect.width || !scene.width) return;
            setGeom({
                left: cRect.left - wRect.left,
                top: cRect.top - wRect.top,
                scale: cRect.width / scene.width,
            });
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(canvas);
        observer.observe(wrapper);
        window.addEventListener('resize', measure);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [canvasRef, wrapperRef, scene.width]);

    if (!geom) return null;

    const selectionMode = !!repositionKey;
    // Text-edit session (sheet open). Reposition wins if both are somehow set.
    const textEditMode = !selectionMode && !!editingKey;

    // Rendered canvas size — the clamp bounds so no marker draws off the board.
    const canvasW = scene.width * geom.scale;
    const canvasH = scene.height * geom.scale;

    // Clamped, INSET-expanded marker box for a field, in canvas coordinates.
    const boxFor = (key: string) => {
        const rect = rects.get(key);
        if (!rect) return null;
        const box = clampRectToCanvas(
            {
                left: rect.x * geom.scale - INSET,
                top: rect.y * geom.scale - INSET,
                width: rect.width * geom.scale + INSET * 2,
                height: rect.height * geom.scale + INSET * 2,
            },
            canvasW,
            canvasH,
        );
        return box.width > 0 && box.height > 0 ? box : null;
    };

    // Selection mode: transparent cancel strips around the selected element
    // (its own area stays uncovered so Konva owns the drag), plus invisible
    // long-press hit areas over the OTHER image fields to switch selection.
    // Text-edit mode uses the SAME strips around the edited element (tap
    // outside = close the sheet), with the normal markers on top so tapping
    // another element switches straight to editing it.
    const focusKey = selectionMode ? repositionKey! : (textEditMode ? editingKey! : null);
    const selectedBox = focusKey ? boxFor(focusKey) : null;
    // The overlay hole around the focused element. Reposition shows Transformer
    // handles, so the hole grows by the handle clearance — rotate handle above
    // for an unrotated element, but once rotated it swings around the box and
    // can poke out of ANY side of the AABB, so the clearance goes uniform.
    // Text-edit has no Transformer and keeps the tight box.
    const focusRotated = !!focusKey && ((rects.get(focusKey)?.rotation ?? 0) % 360 !== 0);
    const sideClearance = focusRotated ? ROTATE_CLEARANCE : HANDLE_CLEARANCE;
    const holeBox: Box2D | null = selectedBox && selectionMode
        ? clampRectToCanvas(
            {
                left: selectedBox.left - sideClearance,
                top: selectedBox.top - ROTATE_CLEARANCE,
                width: selectedBox.width + sideClearance * 2,
                height: selectedBox.height + sideClearance + ROTATE_CLEARANCE,
            },
            canvasW,
            canvasH,
        )
        : selectedBox;
    const cancelStrips = () => {
        // Fall back to a full-canvas strip if the selected element has no rect.
        const b = holeBox ?? {left: 0, top: 0, width: 0, height: 0};
        const strips = [
            {left: 0, top: 0, width: canvasW, height: b.top},                                        // above
            {left: 0, top: b.top + b.height, width: canvasW, height: canvasH - b.top - b.height},    // below
            {left: 0, top: b.top, width: b.left, height: b.height},                                  // start side
            {left: b.left + b.width, top: b.top, width: canvasW - b.left - b.width, height: b.height}, // end side
        ];
        return strips
            .filter((s) => s.width > 0.5 && s.height > 0.5)
            .map((s, i) => (
                <Box
                    key={`cancel-strip-${i}`}
                    data-testid="fill-cancel-strip"
                    onClick={() => {
                        if (clickSuppressed()) return;
                        onCancelSelection?.();
                    }}
                    // Inline style for geometry (RTL emotion cache flips sx left).
                    style={{left: geom.left + s.left, top: geom.top + s.top, width: s.width, height: s.height}}
                    // touch-action none, not pan-y: during reposition the board
                    // owns every gesture — a pinch finger landing on a strip must
                    // reach the renderer's window-level zoom handler, not start a
                    // page scroll. (In text-edit mode there's no pinch, but a
                    // stray scroll from a tap-to-close strip is no loss either.)
                    sx={{position: 'absolute', pointerEvents: 'auto', touchAction: selectionMode ? 'none' : 'pan-y'}}
                />
            ));
    };

    return (
        // pointer-events pass through the gaps; only the markers themselves take
        // taps, and pan-y keeps vertical scroll working over them.
        <Box sx={{position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2, touchAction: 'pan-y'}}>
            {(selectionMode || textEditMode) && !relayKey && cancelStrips()}
            {fields.map((field, index) => {
                // During a relay only the relaying marker exists (it must survive;
                // everything else would swallow/steal the live pointer stream).
                if (relayKey) {
                    if (field.key !== relayKey) return null;
                } else if (selectionMode) {
                    // Selection mode: hit areas for every OTHER field (image AND
                    // text) — long-press switches to that element; tap cancels.
                    if (field.key === repositionKey) return null;
                }

                const box = boxFor(field.key);
                if (!box) return null;

                // Selection mode: a neighbouring field's hit area may overlap the
                // Transformer-handle clearance around the selected element — carve
                // that band out (long-press-to-switch keeps working on the rest of
                // the field) so handle-drags reach the canvas. Not while relaying:
                // the relay marker must stay whole under the live pointer.
                const boxes = !relayKey && selectionMode && holeBox ? subtractRect(box, holeBox) : [box];

                const done = isFieldFilled(field, selections[field.key]);
                // Chip scales with the (clamped) marker so tiny fields don't get a
                // giant chip and big ones don't get a lost one.
                const chipSize = Math.max(14, Math.min(22, Math.round(box.height * 0.55)));
                // Center the chip on the marker's top-left corner, but if that
                // corner hugs the board edge, tuck the chip inward (like the
                // prototype's full-bleed hero chip sitting inside the artwork).
                const chipLeft = box.left < 4 ? 4 : -chipSize / 2;
                const chipTop = box.top < 4 ? 4 : -chipSize / 2;

                return boxes.map((piece, pieceIndex) => (
                    <Marker
                        key={`${field.key}:${pieceIndex}`}
                        field={field}
                        number={numbers?.get(field.key) ?? index + 1}
                        done={done}
                        chipSize={chipSize}
                        chipLeft={chipLeft}
                        chipTop={chipTop}
                        boxStyle={{
                            left: geom.left + piece.left,
                            top: geom.top + piece.top,
                            width: piece.width,
                            height: piece.height,
                        }}
                        relaying={field.key === relayKey}
                        invisible={selectionMode}
                        active={textEditMode && field.key === editingKey}
                        canvasRef={canvasRef}
                        onTap={selectionMode
                            ? () => {
                                if (clickSuppressed()) return;
                                onCancelSelection?.();
                            }
                            : onMarkerTap}
                        onLongPress={onMarkerLongPress}
                        onRelayStart={(f) => setRelayKey(f.key)}
                        onRelayEnd={() => {
                            relayEndAtRef.current = Date.now();
                            setRelayKey(null);
                        }}
                    />
                ));
            })}
        </Box>
    );
};

export default CanvasMarkers;
